import { localDateTimeKey } from '../lib/time'
import type { TaskDashboard, TaskPeriodicity, TaskPriority, TaskSource, TaskStatus } from '../types/domain'

export const statusLabels: Record<TaskStatus, string> = {
  not_started: 'Não iniciada',
  in_progress: 'Em andamento',
  completed: 'Concluída',
}

export const sourceLabels: Record<TaskSource, string> = {
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  phone: 'Telefone',
  in_person: 'Pessoalmente',
  other: 'Outras',
}

export const priorityLabels: Record<TaskPriority, string> = {
  normal: 'Normal',
  urgent: 'Urgente',
}

export const periodicityLabels: Record<TaskPeriodicity, string> = {
  none: 'Sem periodicidade',
  weekly: 'Semanal',
  monthly: 'Mensal',
  annual: 'Anual',
}

export function nextStatus(
  current: TaskStatus,
  command: 'start' | 'complete' | 'reopen',
): TaskStatus {
  if (command === 'complete') return 'completed'
  if (command === 'reopen' && current === 'completed') return 'in_progress'
  if (command === 'start' && current === 'not_started') return 'in_progress'
  return current
}

export function isDueBeforeReceipt(values: {
  receivedDate: string
  receivedTime: string
  dueDate: string
  dueTime: string
}): boolean {
  if (!values.receivedDate || !values.receivedTime || !values.dueDate) return false
  return localDateTimeKey(values.dueDate, values.dueTime || null)
    < localDateTimeKey(values.receivedDate, values.receivedTime)
}

export function taskTitleClass(task: Pick<TaskDashboard, 'archived_at' | 'status' | 'is_overdue'>): string {
  if (task.archived_at || task.status === 'completed') return 'text-slate-700'
  return task.is_overdue ? 'text-red-700' : 'text-blue-700'
}

function compactDate(value: string): string {
  return value.replaceAll('-', '')
}

function nextCalendarDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + 1))
  return [
    String(next.getUTCFullYear()).padStart(4, '0'),
    String(next.getUTCMonth() + 1).padStart(2, '0'),
    String(next.getUTCDate()).padStart(2, '0'),
  ].join('')
}

function timedCalendarRange(date: string, time: string): string {
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = time.split(':').map(Number)
  const start = new Date(Date.UTC(year, month - 1, day, hour, minute))
  const end = new Date(start.getTime() + 60 * 60 * 1000)
  const format = (value: Date) => [
    String(value.getUTCFullYear()).padStart(4, '0'),
    String(value.getUTCMonth() + 1).padStart(2, '0'),
    String(value.getUTCDate()).padStart(2, '0'),
    'T',
    String(value.getUTCHours()).padStart(2, '0'),
    String(value.getUTCMinutes()).padStart(2, '0'),
    '00',
  ].join('')
  return `${format(start)}/${format(end)}`
}

export function googleCalendarUrl(task: Pick<
  TaskDashboard,
  'title' | 'description' | 'client_name' | 'assignee_name' | 'department_name' | 'due_date' | 'due_time'
>): string {
  const dates = task.due_time
    ? timedCalendarRange(task.due_date, task.due_time.slice(0, 5))
    : `${compactDate(task.due_date)}/${nextCalendarDate(task.due_date)}`
  const details = [
    task.description?.trim(),
    `Cliente: ${task.client_name}`,
    `Responsável: ${task.assignee_name}`,
    `Departamento: ${task.department_name}`,
  ].filter(Boolean).join('\n')
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: task.title,
    details,
    dates,
    ctz: 'America/Sao_Paulo',
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}
